import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Scale, X } from "lucide-react";
import { TagInput } from "@/components/ui/tag-input";
import {
  useCreateLegalReference,
  useUpdateLegalReference,
  type LegalReference,
} from "@/hooks/use-legal";

// Mejora N3 (changelog cliente): diálogo para crear/editar una norma
// custom del catálogo legal. Reusa el mismo formulario para ambos modos:
// si `reference` viene definido es edición (slug bloqueado).
//
// Mejora "simplificar formulario" (changelog cliente): solo se piden los
// campos imprescindibles; el resto (slug, categoría, fecha de publicación,
// BOE ID, notas) vive en un bloque colapsable "Opciones avanzadas". El
// slug y la fecha de publicación se auto-derivan en el backend si se dejan
// vacíos.

interface CustomLegalReferenceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  reference?: LegalReference | null;
}

const STATUS_OPTIONS: { value: "vigente" | "derogada" | "modificada"; label: string }[] = [
  { value: "vigente", label: "Vigente" },
  { value: "modificada", label: "Modificada" },
  { value: "derogada", label: "Derogada" },
];

const SLUG_PATTERN = /^[a-z0-9_]+$/;

interface FormState {
  slug: string;
  title: string;
  short_title: string;
  number: string;
  boe_id: string;
  publication_date: string;
  consolidated_date: string;
  status: "vigente" | "derogada" | "modificada";
  url: string;
  category: string;
  aliases: string[];
  superseded_by: string;
  notes: string;
}

const EMPTY_STATE: FormState = {
  slug: "",
  title: "",
  short_title: "",
  number: "",
  boe_id: "",
  publication_date: "",
  consolidated_date: "",
  status: "vigente",
  url: "",
  category: "",
  aliases: [],
  superseded_by: "",
  notes: "",
};

function fromReference(ref: LegalReference): FormState {
  return {
    slug: ref.id,
    title: ref.title,
    short_title: ref.short_title,
    number: ref.number,
    boe_id: ref.boe_id ?? "",
    publication_date: ref.publication_date,
    consolidated_date: ref.consolidated_date,
    status: ref.status,
    url: ref.url,
    category: ref.category,
    aliases: ref.aliases ?? [],
    superseded_by: ref.superseded_by ?? "",
    notes: ref.notes ?? "",
  };
}

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

export function CustomLegalReferenceDialog({
  isOpen,
  onClose,
  reference,
}: CustomLegalReferenceDialogProps) {
  const isEdit = !!reference;
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [formError, setFormError] = useState<string | null>(null);
  // Bloque de "Opciones avanzadas" (mejora "simplificar formulario").
  // En edición lo abrimos por defecto para no esconder datos existentes.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const createMutation = useCreateLegalReference();
  const updateMutation = useUpdateLegalReference();
  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setForm(reference ? fromReference(reference) : EMPTY_STATE);
    setFormError(null);
    setAdvancedOpen(!!reference); // edición: mostrar todo
    resetCreate();
    resetUpdate();
  }, [isOpen, reference, resetCreate, resetUpdate]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    // El slug es opcional al crear (se auto-deriva del número en el
    // backend). Solo validamos el patrón si el usuario escribió uno.
    if (!isEdit && form.slug.trim() && !SLUG_PATTERN.test(form.slug.trim())) {
      setFormError(
        "El identificador debe contener solo minúsculas, números y guiones bajos."
      );
      return;
    }
    if (!form.title.trim() || !form.short_title.trim() || !form.number.trim()) {
      setFormError("Título, título corto y número son obligatorios.");
      return;
    }
    if (!form.consolidated_date) {
      setFormError("La fecha es obligatoria.");
      return;
    }
    if (!form.url.trim() || form.url.trim().length < 8) {
      setFormError("La URL oficial es obligatoria.");
      return;
    }
    setFormError(null);

    const payload = {
      title: form.title.trim(),
      short_title: form.short_title.trim(),
      number: form.number.trim(),
      boe_id: form.boe_id.trim() || null,
      // Si no se indica fecha de publicación, el backend usa la de
      // consolidación.
      publication_date: form.publication_date || null,
      consolidated_date: form.consolidated_date,
      status: form.status,
      url: form.url.trim(),
      // Categoría por defecto si el usuario no la rellena.
      category: form.category.trim() || "general",
      aliases: form.aliases,
      superseded_by: form.superseded_by.trim() || null,
      notes: form.notes.trim() || null,
    };

    const onSuccess = () => onClose();

    if (isEdit && reference) {
      updateMutation.mutate({ slug: reference.id, data: payload }, { onSuccess });
    } else {
      // slug se envía solo si el usuario lo escribió; si no, el backend
      // lo deriva del número.
      const slug = form.slug.trim();
      createMutation.mutate(
        { ...payload, ...(slug ? { slug } : {}) },
        { onSuccess }
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? "Editar norma" : "Añadir norma"}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? "Editar norma del catálogo" : "Añadir norma al catálogo"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* ---------- Campos esenciales ---------- */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título oficial
            </label>
            <textarea
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              rows={2}
              placeholder="Decreto 12/2024 de protección de menores en Andalucía"
              disabled={isSaving}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título corto
              </label>
              <input
                type="text"
                value={form.short_title}
                onChange={(e) => update("short_title", e.target.value)}
                placeholder="Decreto AND 12/2024"
                disabled={isSaving}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número
              </label>
              <input
                type="text"
                value={form.number}
                onChange={(e) => update("number", e.target.value)}
                placeholder="Decreto 12/2024"
                disabled={isSaving}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha{" "}
                <span className="text-gray-400 font-normal">
                  versión vigente
                </span>
              </label>
              <input
                type="date"
                value={form.consolidated_date}
                onChange={(e) => update("consolidated_date", e.target.value)}
                disabled={isSaving}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={form.status}
                onChange={(e) =>
                  update("status", e.target.value as FormState["status"])
                }
                disabled={isSaving}
                className={inputClass}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL oficial (BOE / boletín)
            </label>
            <input
              type="url"
              value={form.url}
              onChange={(e) => update("url", e.target.value)}
              placeholder="https://www.boe.es/buscar/act.php?id=..."
              disabled={isSaving}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aliases{" "}
              <span className="text-gray-400 font-normal">
                cómo se cita coloquialmente
              </span>
            </label>
            <TagInput
              tags={form.aliases}
              onChange={(tags) => update("aliases", tags)}
              placeholder="Escribe un alias y pulsa Enter (ej: LCSP, Ley 9/2017…)"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              El verificador legal usa estos alias para detectar la norma en
              tus propuestas.
            </p>
          </div>

          {form.status === "derogada" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sustituida por{" "}
                <span className="text-gray-400 font-normal">
                  slug de la norma sustituta
                </span>
              </label>
              <input
                type="text"
                value={form.superseded_by}
                onChange={(e) => update("superseded_by", e.target.value)}
                placeholder="lcsp_2017"
                disabled={isSaving}
                className={inputClass}
              />
            </div>
          )}

          {/* ---------- Opciones avanzadas (colapsable) ---------- */}
          <div className="border-t border-gray-100 pt-2">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              {advancedOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              Opciones avanzadas
            </button>

            {advancedOpen && (
              <div className="mt-3 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Identificador (slug){" "}
                      <span className="text-gray-400 font-normal">opcional</span>
                    </label>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) =>
                        update("slug", e.target.value.toLowerCase())
                      }
                      placeholder="se deriva del número si lo dejas vacío"
                      disabled={isSaving || isEdit}
                      className={`${inputClass} disabled:bg-gray-100 disabled:text-gray-500`}
                      title={
                        isEdit
                          ? "El identificador no se puede cambiar tras crear la norma"
                          : undefined
                      }
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Minúsculas, números y guiones bajos. No se puede cambiar
                      tras crear la norma.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoría{" "}
                      <span className="text-gray-400 font-normal">opcional</span>
                    </label>
                    <input
                      type="text"
                      value={form.category}
                      onChange={(e) => update("category", e.target.value)}
                      placeholder="general (por defecto)"
                      disabled={isSaving}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de publicación{" "}
                      <span className="text-gray-400 font-normal">opcional</span>
                    </label>
                    <input
                      type="date"
                      value={form.publication_date}
                      onChange={(e) =>
                        update("publication_date", e.target.value)
                      }
                      disabled={isSaving}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      BOE ID{" "}
                      <span className="text-gray-400 font-normal">opcional</span>
                    </label>
                    <input
                      type="text"
                      value={form.boe_id}
                      onChange={(e) => update("boe_id", e.target.value)}
                      placeholder="BOE-A-2024-12345"
                      disabled={isSaving}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas{" "}
                    <span className="text-gray-400 font-normal">
                      internas, no se usan en el verificador
                    </span>
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    rows={2}
                    placeholder="Contexto interno: por qué se añade esta norma…"
                    disabled={isSaving}
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>

          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {formError}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Guardar cambios" : "Añadir norma"}
          </button>
        </div>
      </div>
    </div>
  );
}
